import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import Head from 'next/head'
import Link from 'next/link'
import { useId, useState } from 'react'
import { ArrowRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CONTACT } from '@/constants/site'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'

interface FaqListProps {
    items: Faq[]
    index?: string
    title?: ReactNode
    className?: string
    tone?: string
    id?: string
}

export const FaqList: FC<FaqListProps> = ({
    items,
    index,
    title = 'Ofte stilte spørsmål',
    className,
    tone = 'tone-paper',
    id = 'faq'
}): ReactNode => {
    const base = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const [open, setOpen] = useState<number[]>([0])

    const toggle = (i: number): void => {
        setOpen(current => current.includes(i) ? current.filter(n => n !== i) : [...current, i])
    }

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer.join(' ') }
        }))
    }

    return (
        <section id={id} className={cn(tone, 'relative py-24 md:py-36', className)} data-header={tone.includes('night') || tone.includes('deep') ? 'dark' : 'light'}>
            <Head>
                <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
            </Head>

            <div className='wrap grid grid-cols-12 gap-x-6 gap-y-12'>
                <div className='col-span-12 lg:col-span-4'>
                    <div className='lg:sticky lg:top-28'>
                        <SectionLabel index={index}>Spørsmål og svar</SectionLabel>
                        <h2 className='t-h1 mt-6 balance' data-reveal='words'>
                            {typeof title === 'string' ? <SplitWords text={title} /> : title}
                        </h2>
                        <p className='mt-6 max-w-sm text-tone-muted'>
                            Finner du ikke svaret? Ring oss på{' '}
                            <a href={CONTACT.phoneHref} className='u-link-static font-semibold text-tone-fg'>{CONTACT.phone}</a>
                            , eller{' '}
                            <Link href='/kontakt' className='u-link-static font-semibold text-tone-fg'>ta kontakt</Link>.
                        </p>
                    </div>
                </div>

                <ul className='col-span-12 border-t border-tone-line lg:col-span-7 lg:col-start-6'>
                    {items.map((item, i) => {
                        const isOpen = open.includes(i)
                        const buttonId = `${base}-q-${i}`
                        const panelId = `${base}-a-${i}`

                        return (
                            <li key={item.question} className='border-b border-tone-line'>
                                <h3>
                                    <button
                                        id={buttonId}
                                        type='button'
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        onClick={() => toggle(i)}
                                        className='group flex w-full items-start gap-5 py-6 text-left md:py-7'
                                    >
                                        <span className='t-label tabular mt-1.5 w-6 shrink-0 text-tone-muted'>
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <span className='t-h3 flex-1 transition-transform duration-500 ease-out-quint group-hover:translate-x-1'>
                                            {item.question}
                                        </span>
                                        <span className={cn('mt-1 flex size-8 shrink-0 items-center justify-center border border-tone-line transition-colors duration-500', isOpen && 'border-tone-accent bg-tone-accent text-tone-accent-fg')}>
                                            <Plus className={cn('size-4 transition-transform duration-500 ease-out-quint', isOpen && 'rotate-45')} aria-hidden='true' />
                                        </span>
                                    </button>
                                </h3>

                                <div id={panelId} role='region' aria-labelledby={buttonId} className='accordion-panel' data-open={isOpen}>
                                    <div>
                                        <div className='flex flex-col gap-4 pr-12 pb-8 pl-11 text-tone-muted md:text-[1.0625rem]'>
                                            {item.answer.map(paragraph => (
                                                <p key={paragraph} className='max-w-2xl leading-relaxed'>{paragraph}</p>
                                            ))}

                                            {item.link && (
                                                <Link href={item.link.href} className='arrow-link inline-flex w-fit items-center gap-2 font-semibold text-tone-fg' tabIndex={isOpen ? 0 : -1}>
                                                    <span className='u-link-static'>{item.link.label}</span>
                                                    <ArrowRight className='arrow-link-icon size-4' aria-hidden='true' />
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}
